import { Check, Copy, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface ManagedFlowCardProps {
  managerEmail: string | null;
  onIveAdded: () => void;
  isLoading: boolean;
}

export function ManagedFlowCard({ managerEmail, onIveAdded, isLoading }: ManagedFlowCardProps) {
  const { t } = useTranslation("settings");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!managerEmail) return;
    try {
      await navigator.clipboard.writeText(managerEmail);
      setCopied(true);
      toast.success(t("reviews.connect.managed.emailCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("reviews.connect.managed.emailCopyFailed"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-amber-50 dark:bg-amber-950 p-2">
            <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <CardTitle>{t("reviews.connect.managed.title")}</CardTitle>
            <CardDescription>{t("reviews.connect.managed.description")}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="text-sm space-y-3 list-decimal list-inside">
          <li>
            {t("reviews.connect.managed.step1Before")}{" "}
            <a
              href="https://business.google.com"
              target="_blank"
              rel="noreferrer"
              className="underline text-primary"
            >
              business.google.com
            </a>{" "}
            {t("reviews.connect.managed.step1After")}
          </li>
          <li>
            {t("reviews.connect.managed.step2")}
            <div className="mt-2 flex items-center gap-2 max-w-md">
              <div className="flex-1 rounded-md border bg-muted/40 px-3 py-2 font-mono text-sm break-all">
                {managerEmail ?? "—"}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!managerEmail}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </li>
          <li>{t("reviews.connect.managed.step3")}</li>
        </ol>

        {!managerEmail ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800 p-3 text-sm">
            {t("reviews.connect.managed.noManagerAvailable")}
          </div>
        ) : null}

        <div>
          <Button onClick={onIveAdded} disabled={isLoading || !managerEmail}>
            {isLoading
              ? t("reviews.connect.managed.actionLoading")
              : t("reviews.connect.managed.action")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
